import { useEffect, useState } from 'react';
import {
  addCourseOfferAnnouncement,
  getSingleCourseOffersAnnouncement,
  getCourseOffersAnnouncements,
  updateCourseOfferAnnouncement,
  sendBid,
  cancelBid,
  updateSelectedTrainers,
  sendRetractRequestFromCourseOffer,
  getAvailableTrainers,
} from '../services/courseOfferAnnouncementService';
import { getCourses } from '../services/courseService';
import { getCourseOffers } from '../services/courseOfferService';

import {
  ERROR,
  IDLE,
  SUCCESS,
  SUCCESS_MESSAGE,
  GENERIC_ERROR_MESSAGE,
  REQUEST_RETRACT_SUCCESS_MESSAGE,
  REQUEST_RETRACT_SUCCESS,
} from '../utilities/constants';

const defaultParams = {
  courseOffers: {},
};
const defaultSearchParams = [1, 20];

const useCourseOfferAnnouncementsData = (params = defaultParams) => {
  const [fetching, setFetching] = useState(false);
  const [status, setStatus] = useState({ status: IDLE });
  const [courses, setCourses] = useState([]);
  const [courseOffers, setCourseOffers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [courseOfferAnnouncements, setCourseOfferAnnouncements] = useState([]);
  const [
    selectedCourseOfferAnnouncement,
    setSelectedCourseOfferAnnouncement,
  ] = useState([]);

  const handleFetchingError = (error) => {
    if (error?.response?.data?.message)
      setStatus({ type: ERROR, message: error.response.data.message });
    else setStatus({ type: ERROR, message: GENERIC_ERROR_MESSAGE });
  };

  const fetchCourseOffers = (params) => {
    const preformattedParams = Object.assign(
      { courseOfferStatus: ['Active', 'Pending'] },
      params
    );
    getCourseOffers(0, 0, preformattedParams)
      .then((response) => {
        const courseOfferList = response?.data?.paginatedList.map(
          ({ code, _id, wave }) => ({ code, courseOfferId: _id, wave })
        );

        setCourseOffers(courseOfferList);
        setStatus({ type: SUCCESS, message: SUCCESS_MESSAGE });
      })
      .catch(handleFetchingError);
  };

  const fetchCourses = () => {
    setFetching(true);
    getCourses()
      .then((response) => {
        const coursesList = response?.data?.paginatedList.map(
          ({ _id, title, courseId, mode }) => ({
            _id,
            title: `${title} (${mode})`,
            courseId,
          })
        );
        setCourses(coursesList);
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  const fetchTrainers = (courseOffersId) => {
    getAvailableTrainers(courseOffersId)
      .then((response) => {
        const trainersList = response?.data?.map(
          ({ firstName, lastName, _id }) => ({
            name: `${firstName} ${lastName}`,
            id: _id,
          })
        );

        setTrainers(trainersList);
        setStatus({ type: SUCCESS, message: SUCCESS_MESSAGE });
      })
      .catch((error) => {
        console.error(error.message);
      });
  };

  const fetchCourseOfferAnnouncements = (pageNo, pageSize, params) => {
    setFetching(true);
    getCourseOffersAnnouncements(pageNo, pageSize, params)
      .then((response) => {
        const data = response?.data?.paginatedList.map((item) => {
          item.leadTrainerBidsCount = item?.leadTrainer?.bids?.length || 0;
          item.assistantTrainerBidsCount =
            item?.assistantTrainer?.bids?.length || 0;
          item.supportTrainersBidsCount =
            item?.supportTrainers?.bids?.length || 0;
          return item;
        });

        setCourseOfferAnnouncements(data);
        setTotalCount(response?.data?.totalCount);

        setStatus({ type: SUCCESS, message: SUCCESS_MESSAGE });
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  const fetchSingleCourseOfferAnnouncement = (id) => {
    setFetching(true);
    getSingleCourseOffersAnnouncement(id)
      .then((response) => {
        setSelectedCourseOfferAnnouncement(response.data);
        setStatus({ type: SUCCESS, message: SUCCESS_MESSAGE });
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  const createCourseOfferAnnouncement = (params) => {
    setFetching(true);
    addCourseOfferAnnouncement(params)
      .then(() => {
        fetchCourseOfferAnnouncements(...defaultSearchParams);
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  const sendCourseOfferAnnouncementUpdate = (id, params) => {
    setFetching(true);
    updateCourseOfferAnnouncement(id, params)
      .then(() => {
        fetchSingleCourseOfferAnnouncement(id);
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  const createBid = (id, role) => {
    setFetching(true);
    sendBid(id, role)
      .then(() => {
        fetchCourseOfferAnnouncements(...defaultSearchParams);
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  const deleteBid = (id, role) => {
    setFetching(true);

    cancelBid(id, role)
      .then(() => {
        fetchCourseOfferAnnouncements(...defaultSearchParams);
      })
      .catch(handleFetchingError)
      .then(() => setFetching(false));
  };

  const selectTrainers = (params, cb = () => {}) => {
    setFetching(true);

    updateSelectedTrainers(selectedCourseOfferAnnouncement?._id, params)
      .then(() => fetchCourseOfferAnnouncements(...defaultSearchParams))
      .then(() => {
        cb();
      })
      .catch(handleFetchingError)
      .then(() => setFetching(false));
  };

  const requestRetractFromCourseOffer = (id, body) => {
    setFetching(true);
    sendRetractRequestFromCourseOffer(id, body)
      .then(() => {
        setStatus({
          type: REQUEST_RETRACT_SUCCESS,
          message: REQUEST_RETRACT_SUCCESS_MESSAGE,
        });
      })
      .catch(handleFetchingError)
      .then(() => {
        setFetching(false);
      });
  };

  useEffect(() => {
    fetchCourses();
    fetchCourseOfferAnnouncements(...defaultSearchParams);
  }, []);

  return {
    fetching,
    status,
    courseOffers,
    trainers,
    courseOfferAnnouncements,
    retryCourseOffersFetch: (params) => {
      fetchCourseOffers(params);
    },
    retryTrainersFetch: (id) => {
      if (id) fetchTrainers(id);
    },
    retryCoureOfferAnnouncementsFetch: (pageNo, pageSize, params) =>
      fetchCourseOfferAnnouncements(pageNo, pageSize, params),
    createCourseOfferAnnouncement,
    fetchSingleCourseOfferAnnouncement,
    selectedCourseOfferAnnouncement,
    sendCourseOfferAnnouncementUpdate,
    createBid,
    deleteBid,
    selectTrainers,
    courses,
    requestRetractFromCourseOffer,
    totalCount,
  };
};

export default useCourseOfferAnnouncementsData;
